import React, { useState } from "react";
import { View, Text, TextInput, Button, StyleSheet, Alert } from "react-native";
import { TSession } from "../../../../utils/types";
import {
  TCreateIntegrationRequest,
  TPipedriveOAuthCredentials,
  TPipedriveApiKeyCredentials,
  createIntegration,
} from "../../../../services/api-integrations";
import { IntegrationType } from "../../../../utils/integrationTypes";

type Props = {
  session: TSession;
  onSuccess?: () => void;
};

const IntegrationSetupForm: React.FC<Props> = ({ session, onSuccess }) => {
  const [selectedCRM, setSelectedCRM] = useState<IntegrationType | null>(null);
  const [method, setMethod] = useState<"oauth" | "api_key">("oauth");

  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [apiKey, setApiKey] = useState("");

  // Pipedrive-specific fields
  const [createAs, setCreateAs] = useState<"lead" | "deal">("lead");
  const [pipelineId, setPipelineId] = useState("");
  const [stageId, setStageId] = useState("");
  const [userId, setUserId] = useState("");

  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!session || !selectedCRM) return;

    let credentials: TPipedriveOAuthCredentials | TPipedriveApiKeyCredentials;
    let integration_type: IntegrationType = selectedCRM;
    let config: Record<string, any> | undefined;

    if (
      selectedCRM === IntegrationType.PIPEDRIVE_OAUTH ||
      selectedCRM === IntegrationType.PIPEDRIVE_API_KEY
    ) {
      config = {
        create_as: createAs,
        pipeline_id: pipelineId ? parseInt(pipelineId) : undefined,
        stage_id: stageId ? parseInt(stageId) : undefined,
        user_id: userId ? parseInt(userId) : undefined,
      };

      integration_type =
        method === "oauth"
          ? IntegrationType.PIPEDRIVE_OAUTH
          : IntegrationType.PIPEDRIVE_API_KEY;

      credentials =
        method === "oauth"
          ? { client_id: clientId.trim(), client_secret: clientSecret.trim() }
          : { api_key: apiKey.trim() };
    } else {
      Alert.alert("Unsupported", "Only Pipedrive is supported for now.");
      return;
    }

    const payload: TCreateIntegrationRequest = {
      integration_type,
      credentials,
      config,
      is_active: true,
    };

    try {
      setLoading(true);
      const response = await createIntegration(session, payload);
      Alert.alert(
        "Success",
        `Integration created (ID: ${response.integration_id})`,
      );
      onSuccess?.();
    } catch (error) {
      console.error("Error creating integration:", error);
      Alert.alert("Error", String(error));
    } finally {
      setLoading(false);
    }
  };
  const renderPipedriveInputs = () => (
    <View style={styles.subSection}>
      <Text style={styles.label}>Authentication Method:</Text>
      <View style={styles.toggleContainer}>
        <Button
          title="OAuth"
          color={method === "oauth" ? "#00B7F4" : "#ccc"}
          onPress={() => setMethod("oauth")}
        />
        <Button
          title="API Key"
          color={method === "api_key" ? "#00B7F4" : "#ccc"}
          onPress={() => setMethod("api_key")}
        />
      </View>

      {method === "oauth" ? (
        <>
          <Text style={styles.label}>OAuth Credentials:</Text>
          <TextInput
            placeholder="Client ID"
            value={clientId}
            onChangeText={setClientId}
            style={styles.input}
            autoCapitalize="none"
          />
          <TextInput
            placeholder="Client Secret"
            value={clientSecret}
            onChangeText={setClientSecret}
            style={styles.input}
            autoCapitalize="none"
          />
        </>
      ) : (
        <>
          <Text style={styles.label}>API Key:</Text>
          <TextInput
            placeholder="API Key"
            value={apiKey}
            onChangeText={setApiKey}
            style={styles.input}
            autoCapitalize="none"
          />
        </>
      )}

      <Text style={styles.label}>Create As:</Text>
      <View style={styles.toggleContainer}>
        <Button
          title="Lead"
          color={createAs === "lead" ? "#00B7F4" : "#ccc"}
          onPress={() => setCreateAs("lead")}
        />
        <Button
          title="Deal"
          color={createAs === "deal" ? "#00B7F4" : "#ccc"}
          onPress={() => setCreateAs("deal")}
        />
      </View>

      {createAs === "deal" && (
        <>
          <TextInput
            placeholder="Pipeline ID (optional)"
            value={pipelineId}
            onChangeText={setPipelineId}
            keyboardType="numeric"
            style={styles.input}
          />
          <TextInput
            placeholder="Stage ID (optional)"
            value={stageId}
            onChangeText={setStageId}
            keyboardType="numeric"
            style={styles.input}
          />
          <TextInput
            placeholder="User ID (optional)"
            value={userId}
            onChangeText={setUserId}
            keyboardType="numeric"
            style={styles.input}
          />
        </>
      )}
    </View>
  );

  return (
    <View style={styles.container}>
      <Text style={styles.header}>🔌 Set Up CRM Integration</Text>

      <Text style={styles.label}>Choose a CRM:</Text>
      <View style={styles.toggleContainer}>
        <Button
          title="Pipedrive"
          color={
            selectedCRM === IntegrationType.PIPEDRIVE_OAUTH ||
            selectedCRM === IntegrationType.PIPEDRIVE_API_KEY
              ? "#00B7F4"
              : "#ccc"
          }
          onPress={() => {
            setSelectedCRM(IntegrationType.PIPEDRIVE_OAUTH);
            setMethod("oauth");
          }}
        />
        <Button
          title="GoHighLevel"
          color="#ccc"
          onPress={() =>
            Alert.alert("Coming Soon", "GoHighLevel support is coming soon!")
          }
        />
      </View>

      {selectedCRM?.includes("pipedrive") && renderPipedriveInputs()}

      {selectedCRM && (
        <Button
          title={loading ? "Submitting..." : "Submit"}
          onPress={handleSubmit}
          disabled={loading}
          color="#28A745"
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    padding: 16,
    backgroundColor: "#f1f3f5",
    borderRadius: 10,
    marginTop: 24,
    borderWidth: 1,
    borderColor: "#dee2e6",
  },
  header: {
    fontSize: 18,
    fontWeight: "600",
    marginBottom: 16,
    textAlign: "center",
  },
  label: {
    fontSize: 14,
    fontWeight: "500",
    marginBottom: 8,
  },
  toggleContainer: {
    flexDirection: "row",
    justifyContent: "space-around",
    marginBottom: 16,
  },
  subSection: {
    marginBottom: 16,
  },
  input: {
    height: 40,
    borderColor: "#ced4da",
    borderWidth: 1,
    borderRadius: 6,
    paddingHorizontal: 8,
    marginBottom: 12,
    backgroundColor: "#fff",
  },
});

export default IntegrationSetupForm;
